import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge, Modal, Table } from 'react-bootstrap'
import client from '../../api/client'

function UserDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [formData, setFormData] = useState({
    role: '',
    active: true,
  })

  useEffect(() => {
    fetchUser()
  }, [id])

  const fetchUser = async () => {
    setLoading(true)
    try {
      const response = await client.get(`/admin/api/v1/users/${id}`)
      const userData = response.data
      setUser(userData)
      setFormData({
        role: userData.role || 'user',
        active: userData.active !== false,
      })
    } catch (err) {
      console.error('Error fetching user:', err)
      setError('Erreur lors du chargement de l\'utilisateur')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await client.patch(`/admin/api/v1/users/${id}`, formData)
      setSuccess('Utilisateur mis à jour avec succès')
      fetchUser()
    } catch (err) {
      console.error('Error updating user:', err)
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    try {
      await client.delete(`/admin/api/v1/users/${id}`)
      navigate('/users', { state: { success: 'Utilisateur supprimé avec succès' } })
    } catch (err) {
      console.error('Error deleting user:', err)
      setError(err.response?.data?.detail || 'Erreur lors de la suppression')
      setShowDeleteModal(false)
    } finally {
      setSaving(false)
    }
  }

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'danger',
      influencer: 'warning',
      user: 'primary',
    }
    const labels = {
      admin: 'Admin',
      influencer: 'Créateur',
      user: 'Membre',
    }
    return <Badge bg={colors[role] || 'secondary'}>{labels[role] || role}</Badge>
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'full',
      timeStyle: 'short',
    })
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    )
  }

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Utilisateur non trouvé</Alert>
        <Button variant="secondary" onClick={() => navigate('/users')}>
          Retour à la liste
        </Button>
      </Container>
    )
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mb-2"
            onClick={() => navigate('/users')}
          >
            ← Retour
          </Button>
          <h1 className="h3">Détails de l'utilisateur</h1>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col md={8}>
          {/* User Info Card */}
          <Card className="mb-4">
            <Card.Header>Informations</Card.Header>
            <Card.Body>
              <Table borderless>
                <tbody>
                  <tr>
                    <th width="200">Email</th>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <th>Nom</th>
                    <td>{user.name || '-'}</td>
                  </tr>
                  <tr>
                    <th>Rôle actuel</th>
                    <td>{getRoleBadge(user.role)}</td>
                  </tr>
                  <tr>
                    <th>Statut</th>
                    <td>
                      {user.active !== false ? (
                        <Badge bg="success">Actif</Badge>
                      ) : (
                        <Badge bg="secondary">Inactif</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Créé le</th>
                    <td>{formatDate(user.created_at)}</td>
                  </tr>
                  <tr>
                    <th>Dernière connexion</th>
                    <td>{formatDate(user.last_login)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Subscription Info */}
          <Card className="mb-4">
            <Card.Header>Abonnement</Card.Header>
            <Card.Body>
              {user.subscription_plan ? (
                <Table borderless>
                  <tbody>
                    <tr>
                      <th width="200">Plan</th>
                      <td><Badge bg="info">{user.subscription_plan}</Badge></td>
                    </tr>
                    <tr>
                      <th>Statut</th>
                      <td>
                        {user.subscription_status === 'active' ? (
                          <Badge bg="success">Actif</Badge>
                        ) : (
                          <Badge bg="warning">{user.subscription_status || 'Inconnu'}</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Expire le</th>
                      <td>{formatDate(user.subscription_expires)}</td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-0">Aucun abonnement actif</p>
              )}
            </Card.Body>
          </Card>

          {/* Usage Stats */}
          {user.usage_stats && (
            <Card>
              <Card.Header>Statistiques d'utilisation</Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <th width="200">Requêtes totales</th>
                      <td>{(user.usage_stats.total_requests || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <th>Tokens utilisés</th>
                      <td>{(user.usage_stats.total_tokens || 0).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <th>Coût total</th>
                      <td>${(user.usage_stats.total_cost || 0).toFixed(4)}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={4}>
          {/* Edit Form */}
          <Card>
            <Card.Header>Modifier</Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Rôle</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="user">Membre</option>
                    <option value="influencer">Créateur</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Le rôle détermine les permissions de l'utilisateur
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="active"
                    label="Compte actif"
                    checked={formData.active}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">
                    Les comptes inactifs ne peuvent pas se connecter
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Enregistrement...
                      </>
                    ) : 'Enregistrer'}
                  </Button>

                  <Button
                    variant="outline-danger"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={saving}
                  >
                    Supprimer l'utilisateur
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{user.email}</strong> ?
          </p>
          <p className="text-danger mb-0">
            Cette action est irréversible. Toutes les données associées seront supprimées.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? 'Suppression...' : 'Supprimer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default UserDetail
